// ============================================================================
// useNavFocus (W2-B/X-09, E-07) — sammelt die EINGABEN fuer die fokussierte
// Leiste. Die Entscheidung selbst steht in navItems.ts (isNavFocused); dieser
// Hook liefert nur, was sie braucht:
//   • /api/auth/me: nav_focus_since (Stichtag je Server), account_created_at,
//     own_message_count, leads_table_round
//   • clientPrefs:   „Ich leite eine Spielrunde" (lorath_leads_round)
//
// Die Einstellung ist bewusst eine CLIENT-Einstellung (clientPrefs, Cookie +
// localStorage — WKWebView verliert reine Cookies beim Kaltstart). Keine
// Server-Spalte: Beta und Prod teilen die DB, 7.4 kommt ohne Schemaaenderung
// aus. Geraeteuebergreifend traegt stattdessen `leads_table_round` (ein
// angelegter Tisch schaltet die Bereiche ebenfalls frei).
// ============================================================================

import { useSyncExternalStore } from 'react';
import { useOptionalAuth } from '../auth/AuthProvider';
import { clearPref, readPref, writePref } from '../shared/clientPrefs';
import type { AuthUser } from '../api/types';
import { isNavFocused, type NavFocusInput } from './navItems';

export const LEADS_ROUND_PREF = 'lorath_leads_round';

const subscribers = new Set<() => void>();

function subscribe(callback: () => void): () => void {
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
}

export function readLeadsRoundPref(): boolean {
  try {
    return readPref(LEADS_ROUND_PREF) === '1';
  } catch {
    return false;
  }
}

/** Schaltet die Einstellung und benachrichtigt jede offene Leiste sofort. */
export function writeLeadsRoundPref(on: boolean): void {
  if (on) writePref(LEADS_ROUND_PREF, '1');
  else clearPref(LEADS_ROUND_PREF);
  for (const callback of subscribers) callback();
}

export function useLeadsRoundPref(): boolean {
  return useSyncExternalStore(subscribe, readLeadsRoundPref, () => false);
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}

/** Die Server-Fakten aus /me als Eingabe fuer isNavFocused. */
export function navFocusInputOf(
  user: AuthUser | null | undefined,
  leadsRoundPref: boolean,
): NavFocusInput | null {
  if (!user) return null;
  return {
    focusSince: asString(user.nav_focus_since),
    accountCreatedAt: asString(user.account_created_at),
    ownMessageCount: typeof user.own_message_count === 'number' ? user.own_message_count : null,
    leadsTableRound: user.leads_table_round === true,
    leadsRoundPref,
  };
}

function useAuthedUser(): AuthUser | null {
  const auth = useOptionalAuth();
  return auth?.status === 'authed' ? auth.user ?? null : null;
}

/** true = dieses Konto sieht die fokussierte Leiste (Neukonto, E-07). */
export function useNavFocus(): boolean {
  const user = useAuthedUser();
  const leadsRoundPref = useLeadsRoundPref();
  return isNavFocused(navFocusInputOf(user, leadsRoundPref));
}

/**
 * Review m11: Die Karte „Ich leite eine Spielrunde" hat nur dort eine Wirkung,
 * wo das Konto OHNE die Einstellung die fokussierte Leiste haette. Bestands-
 * konten, Konten ab 30 Nachrichten oder mit Tisch sehen sie nicht — ausser die
 * Einstellung ist schon gesetzt, dann bleibt sie zum Abschalten sichtbar.
 */
export function useLeadsRoundCardVisible(): boolean {
  const user = useAuthedUser();
  const leadsRoundPref = useLeadsRoundPref();
  if (leadsRoundPref) return true;
  return isNavFocused(navFocusInputOf(user, false));
}
